/**
 * RYFE — Whiteboard: Host-seitiges Rendern von Web-Embeds für RYFE-eigene Hosts.
 *
 * Excalidraw sandboxt das Embed-iframe für alle Hosts, die NICHT in seiner
 * eingebauten `ALLOW_SAME_ORIGIN`-Liste stehen, OHNE `allow-same-origin`
 * (packages/excalidraw/components/App.tsx). Eine echte Web-App wie der Dify-Chat
 * braucht aber Cookies/Session/localStorage — also same-origin — und bleibt im
 * Standard-Sandbox leer.
 *
 * Über den `renderEmbeddable`-Prop rendern wir für unsere vertrauenswürdigen
 * Hosts (isRyfeEmbedHost) ein eigenes iframe MIT `allow-same-origin`, ohne die
 * Fork-Pakete zu ändern. Für alle anderen Hosts geben wir `null` zurück → dann
 * greift Excalidraws Standard-iframe unverändert.
 *
 * Sicherheits-Hinweis: `allow-same-origin` + `allow-scripts` erlaubt der Seite,
 * auf ihre eigene Origin zuzugreifen — das ist genau der Sinn (First-Party-Host)
 * und bewusst NUR auf die RYFE-Allowlist beschränkt, nie auf beliebige URLs.
 */

import type { NonDeleted } from "@excalidraw/element/types";
import type { ExcalidrawEmbeddableElement } from "@excalidraw/element/types";

import { isRyfeEmbedHost } from "./embed-allowlist";

import type { ReactElement } from "react";

const RYFE_SANDBOX = [
  "allow-same-origin",
  "allow-scripts",
  "allow-forms",
  "allow-popups",
  "allow-popups-to-escape-sandbox",
  "allow-presentation",
  "allow-downloads",
  "allow-modals",
].join(" ");

/**
 * `renderEmbeddable`-Prop: eigenes iframe (mit same-origin) für RYFE-Hosts, sonst
 * `null` (→ Excalidraw-Standard). Der einzubettende Link steht in `element.link`.
 */
export function renderRyfeEmbeddable(
  element: NonDeleted<ExcalidrawEmbeddableElement>,
): ReactElement | null {
  const link = element.link || "";
  if (!isRyfeEmbedHost(link)) {
    return null;
  }
  return (
    <iframe
      className="excalidraw__embeddable"
      src={link}
      title="RYFE Embed"
      referrerPolicy="no-referrer-when-downgrade"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; microphone; camera; fullscreen"
      allowFullScreen
      sandbox={RYFE_SANDBOX}
      style={{ width: "100%", height: "100%", border: "none" }}
    />
  );
}
